import { useEffect, useRef } from 'react';
import { useNotifications } from '../notifications/notifications-context';

/**
 * RE-READ A PORTAL PANEL WHEN THE BELL HEARS ABOUT IT.
 *
 * The resident portal only re-read on focus, so a resident looking at Recent
 * visits while the desk signed their guest in saw the bell ring and the list
 * stay exactly as it was. The app already holds ONE socket for the bell
 * (NotificationsProvider); this listens to the list that socket fills, rather
 * than opening a second connection the server would count against the user.
 *
 * ── ONLY WHAT IS NEW SINCE THIS SCREEN OPENED ──
 * Everything on the list when the hook first looks is history. After that an
 * item counts once, by id, and only when it is NEWER THAN THE NEWEST ITEM
 * ALREADY KNOWN: the provider also REPLACES the list with a fresh page (on
 * reconnect, on first load), and the old rows on that page are not news. A row
 * the socket missed while it was down and a later page brought back IS news,
 * and counts.
 *
 * Both sides of that comparison are the SERVER's clock (created_at). Measuring
 * against this device's clock instead meant a phone running a minute fast
 * ignored every arrival for a minute. The device clock is only the fallback
 * for a list that was still empty when the screen opened.
 *
 * ── A BURST IS ONE READ ──
 * The first matching item opens a short window and anything else inside it
 * rides along — a desk that signs in a party of three sends three frames, and
 * the list needs reading once. The window is not restarted by each frame, so
 * a steady stream cannot postpone the read indefinitely.
 *
 * `refetch` is the panel's own reload(), which renders through Staleable: the
 * list stays on screen and dims, it is never swapped for a skeleton.
 */
export const LIVE_REFRESH_WINDOW_MS = 500;

const timeOf = (createdAt: string | undefined): number => Date.parse(createdAt || '');

const newestOf = (items: { created_at?: string }[]): number | null =>
  items.reduce<number | null>((max, item) => {
    const at = timeOf(item.created_at);

    return Number.isNaN(at) ? max : max === null || at > max ? at : max;
  }, null);

export const usePortalLiveRefresh = (
  prefixes: string[],
  refetch: () => void
): void => {
  const { notifications } = useNotifications();
  const latestRefetch = useRef(refetch);
  latestRefetch.current = refetch;
  const watched = useRef(prefixes);
  watched.current = prefixes;
  const mountedAt = useRef(Date.now());
  const newestKnown = useRef<number | null>(null);
  const seen = useRef<Set<string> | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (!seen.current) {
      seen.current = new Set(notifications.map(item => item.id));
      newestKnown.current = newestOf(notifications);

      return;
    }

    const known = seen.current;
    let news = false;
    // Compared against the newest item known BEFORE this batch, so two new
    // rows arriving together both count.
    const baseline = newestKnown.current;
    notifications.forEach(item => {
      if (known.has(item.id)) return;
      known.add(item.id);
      const type = item.event_type || '';
      const at = timeOf(item.created_at);
      // A row with no readable time cannot be proven old; a spare read is
      // cheap, a missed arrival is the bug this hook exists for.
      const fresh =
        Number.isNaN(at) ||
        (baseline !== null ? at > baseline : at >= mountedAt.current);
      if (fresh && watched.current.some(prefix => type.startsWith(prefix))) {
        news = true;
      }
    });
    const newest = newestOf(notifications);
    if (newest !== null && (newestKnown.current === null || newest > newestKnown.current)) {
      newestKnown.current = newest;
    }

    if (news && timer.current === null) {
      timer.current = window.setTimeout(() => {
        timer.current = null;
        latestRefetch.current();
      }, LIVE_REFRESH_WINDOW_MS);
    }
  }, [notifications]);

  useEffect(
    () => () => {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
        // Null, not just cleared: a remount (StrictMode) reuses this ref, and
        // a stale id here would stop every later window from opening.
        timer.current = null;
      }
    },
    []
  );
};
